import React, { useState, useRef, useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Mic, X, Check, Loader2, Sparkles, Volume2, FileText, MessageSquare, StickyNote, RotateCcw, HelpCircle,
  Mail, Smartphone, ReceiptText, ClipboardCheck, DollarSign, ListChecks, CalendarPlus, CalendarClock,
  GitBranch, Phone, Navigation, Users, ExternalLink, Send, CalendarCheck2, CalendarX2, CalendarSync,
} from "lucide-react";
import { toast } from "sonner";
import { voiceApi } from "../../lib/api";
import { useAuth } from "../../contexts/AuthContext";
import VoiceCommandsSheet from "./VoiceCommandsSheet";

const ACTION_META = {
  add_estimate_items: { icon: FileText, label: "New estimate", color: "#2563EB" },
  email_estimate: { icon: Mail, label: "Email estimate", color: "#0EA5E9" },
  text_estimate: { icon: Smartphone, label: "Text estimate", color: "#059669" },
  email_invoice: { icon: ReceiptText, label: "Email invoice", color: "#7C3AED" },
  text_invoice: { icon: ReceiptText, label: "Text invoice", color: "#059669" },
  update_job_status: { icon: ClipboardCheck, label: "Update job", color: "#EA580C" },
  record_payment: { icon: DollarSign, label: "Collect payment", color: "#16A34A" },
  send_message: { icon: MessageSquare, label: "Send message", color: "#7C3AED" },
  add_customer_note: { icon: StickyNote, label: "Customer note", color: "#0891B2" },
  create_task: { icon: ListChecks, label: "New task", color: "#0891B2" },
  create_event: { icon: CalendarPlus, label: "New event", color: "#7C3AED" },
  schedule_job: { icon: CalendarClock, label: "Schedule job", color: "#2563EB" },
  new_estimate: { icon: FileText, label: "New estimate", color: "#2563EB" },
  send_sms_customer: { icon: Smartphone, label: "Send SMS", color: "#059669" },
  add_job_note: { icon: StickyNote, label: "Job note", color: "#0891B2" },
  book_appointment: { icon: CalendarCheck2, label: "Book appointment", color: "#16A34A" },
  cancel_appointment: { icon: CalendarX2, label: "Cancel appointment", color: "#DC2626" },
  reschedule_appointment: { icon: CalendarSync, label: "Reschedule appointment", color: "#EA580C" },
};

const VIEW_META = {
  tasks: { icon: ListChecks, color: "#0891B2" },
  nav: { icon: MessageSquare, color: "#7C3AED" },
  jobs: { icon: CalendarClock, color: "#2563EB" },
  pipeline_cards: { icon: GitBranch, color: "#EA580C" },
  invoices: { icon: ReceiptText, color: "#7C3AED" },
  revenue_summary: { icon: DollarSign, color: "#16A34A" },
  technicians: { icon: Users, color: "#0EA5E9" },
  call: { icon: Phone, color: "#16A34A" },
  message_nav: { icon: MessageSquare, color: "#7C3AED" },
  unread_messages: { icon: Mail, color: "#0891B2" },
  directions: { icon: Navigation, color: "#2563EB" },
  appointments: { icon: CalendarClock, color: "#16A34A" },
  available_slots: { icon: CalendarCheck2, color: "#0EA5E9" },
  error: { icon: X, color: "#DC2626" },
};

const TECH_STATUS_COLOR = {
  Available: "#16A34A", "On Job": "#EA580C", "En Route": "#2563EB", Lunch: "#CA8A04", Off: "#94A3B8",
};

// Auto-stop: 3s of silence after speech sends the command; 3s with no speech
// at all cancels the turn (no processing). Manual stop always stays as a fallback.
const SILENCE_MS = 3000;
const SPEECH_RMS_THRESHOLD = 0.025;

// ── Mobile voice-assistant TTS: ElevenLabs (backend) with a browser
// speechSynthesis fallback if the API call fails. Scoped to this assistant
// only — does not touch phone AI / outbound agent / receptionist voices.
let currentAudio = null;

function stopSpeaking() {
  if (currentAudio) {
    try { currentAudio.pause(); currentAudio.currentTime = 0; } catch { /* noop */ }
    currentAudio = null;
  }
  if (typeof window !== "undefined" && window.speechSynthesis) window.speechSynthesis.cancel();
}

function fallbackBrowserSpeak(text, cb) {
  let done = false;
  const finish = () => { if (done) return; done = true; try { cb && cb(); } catch { /* noop */ } };
  try {
    if (!text || typeof window === "undefined" || !window.speechSynthesis) { finish(); return; }
    window.speechSynthesis.cancel();
    const u = new SpeechSynthesisUtterance(text);
    u.rate = 1.02;
    u.pitch = 1;
    u.onend = finish;
    u.onerror = finish;
    window.speechSynthesis.speak(u);
    setTimeout(finish, Math.max(1900, text.length * 70) + 700);
  } catch {
    finish();
  }
}

function speak(text) {
  speakThen(text, null);
}

// Speak `text` via ElevenLabs TTS, then invoke `cb` when playback finishes.
// Falls back to the browser's speechSynthesis if the ElevenLabs call fails.
async function speakThen(text, cb) {
  let done = false;
  const finish = () => { if (done) return; done = true; try { cb && cb(); } catch { /* noop */ } };
  if (!text) { finish(); return; }
  stopSpeaking();
  try {
    const blob = await voiceApi.tts(text);
    const url = URL.createObjectURL(blob);
    const audio = new Audio(url);
    currentAudio = audio;
    audio.onended = () => { URL.revokeObjectURL(url); if (currentAudio === audio) currentAudio = null; finish(); };
    audio.onerror = () => { URL.revokeObjectURL(url); if (currentAudio === audio) currentAudio = null; fallbackBrowserSpeak(text, finish); };
    await audio.play();
  } catch {
    fallbackBrowserSpeak(text, finish);
  }
}

/**
 * Flowing audio waveform on a canvas. Reacts to real mic level (via analyserRef)
 * while listening; animates smoothly while the AI is "speaking" (no real stream).
 */
function VoiceWaveform({ analyserRef, speaking }) {
  const canvasRef = useRef(null);
  const rafRef = useRef(0);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const resize = () => {
      canvas.width = Math.max(1, canvas.clientWidth * dpr);
      canvas.height = Math.max(1, canvas.clientHeight * dpr);
    };
    resize();
    window.addEventListener("resize", resize);

    let t = 0;
    let smooth = 0.12;
    const freq = new Uint8Array(128);

    const drawWave = (w, h, mid, level, phase, ampMul, alpha, lw) => {
      ctx.beginPath();
      ctx.moveTo(0, mid);
      for (let x = 0; x <= w; x += 3) {
        const nx = x / w;
        const env = Math.exp(-Math.pow((nx - 0.5) / 0.3, 2));
        const y =
          mid +
          Math.sin(nx * Math.PI * 6 + t + phase) * h * 0.34 * level * env * ampMul +
          Math.sin(nx * Math.PI * 13 - t * 1.7 + phase) * h * 0.12 * level * env * ampMul;
        ctx.lineTo(x, y);
      }
      ctx.strokeStyle = `rgba(96,165,250,${alpha})`;
      ctx.lineWidth = lw * dpr;
      ctx.lineJoin = "round";
      ctx.stroke();
    };

    const render = () => {
      rafRef.current = requestAnimationFrame(render);
      t += 0.045;
      const w = canvas.width;
      const h = canvas.height;
      const mid = h / 2;
      ctx.clearRect(0, 0, w, h);

      let target;
      const analyser = analyserRef?.current;
      if (speaking) {
        target = 0.55 + 0.32 * Math.abs(Math.sin(t * 1.3));
      } else if (analyser) {
        analyser.getByteFrequencyData(freq);
        let sum = 0;
        for (let i = 0; i < freq.length; i++) sum += freq[i];
        target = Math.max(0.12, Math.min(1, sum / freq.length / 78));
      } else {
        target = 0.14 + 0.05 * Math.abs(Math.sin(t));
      }
      smooth += (target - smooth) * 0.18;

      drawWave(w, h, mid, smooth, 0, 1, 0.85, 2.2);
      drawWave(w, h, mid, smooth, 1.7, 0.7, 0.4, 2);
      drawWave(w, h, mid, smooth, 3.2, 0.45, 0.2, 1.6);
    };
    render();

    return () => {
      cancelAnimationFrame(rafRef.current);
      window.removeEventListener("resize", resize);
    };
  }, [analyserRef, speaking]);

  return <canvas ref={canvasRef} className="w-full h-full" data-testid="voice-waveform" />;
}

/**
 * Floating voice-agent button + sheet.
 * Props: context = { jobId, customerId, customerName }, placement ("fab" default)
 */
export default function VoiceAgent({ context = {}, className = "", onActionDone, hideLauncher = false }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const firstName = (user?.name || "").trim().split(/\s+/)[0] || "there";
  const [open, setOpen] = useState(false);
  const [phase, setPhase] = useState("idle"); // idle | recording | processing | confirm | executing | done | error
  const [seconds, setSeconds] = useState(0);
  const [result, setResult] = useState(null); // {transcript, action, args, say, needsConfirm, preview} or execute result
  const [errorMsg, setErrorMsg] = useState("");
  const [picks, setPicks] = useState({}); // estimate disambiguation: { itemIndex: pricebookId }
  const [helpOpen, setHelpOpen] = useState(false);
  const [history, setHistory] = useState([]); // multi-turn context: [{role:"user"|"assistant", text}]
  const [textInput, setTextInput] = useState("");

  const mediaRef = useRef(null);
  const chunksRef = useRef([]);
  const startRef = useRef(0);
  const timerRef = useRef(null);
  const streamRef = useRef(null);
  const analyserRef = useRef(null);
  const audioCtxRef = useRef(null);
  const suppressStopRef = useRef(false); // set when the recorder is stopped to submit typed text instead of audio
  const stoppedRef = useRef(false); // set by a hard stop (quit) — makes any in-flight response a no-op
  const hasSpeechRef = useRef(false); // whether audio above the silence threshold has been heard this turn
  const silenceTimerRef = useRef(null);
  const silenceRafRef = useRef(0);

  // Clears the 3s auto-stop/no-speech timer and its RMS-monitoring loop.
  const clearSilenceWatch = () => {
    if (silenceTimerRef.current) { clearTimeout(silenceTimerRef.current); silenceTimerRef.current = null; }
    if (silenceRafRef.current) { cancelAnimationFrame(silenceRafRef.current); silenceRafRef.current = 0; }
  };

  const cleanupStream = useCallback(() => {
    clearSilenceWatch();
    clearInterval(timerRef.current);
    analyserRef.current = null;
    if (audioCtxRef.current) {
      try { audioCtxRef.current.close(); } catch { /* noop */ }
      audioCtxRef.current = null;
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
  }, []);

  useEffect(() => () => cleanupStream(), [cleanupStream]);

  const reset = () => {
    stoppedRef.current = false;
    hasSpeechRef.current = false;
    clearSilenceWatch();
    setPhase("idle");
    setResult(null);
    setErrorMsg("");
    setSeconds(0);
    setPicks({});
    setHistory([]);
    setTextInput("");
  };

  // Acquire the mic stream once and wire up an analyser for the live waveform.
  const acquireStream = useCallback(async () => {
    if (streamRef.current) return streamRef.current;
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    streamRef.current = stream;
    try {
      const AC = window.AudioContext || window.webkitAudioContext;
      if (AC) {
        const actx = new AC();
        audioCtxRef.current = actx;
        const src = actx.createMediaStreamSource(stream);
        const analyser = actx.createAnalyser();
        analyser.fftSize = 256;
        analyser.smoothingTimeConstant = 0.8;
        src.connect(analyser);
        analyserRef.current = analyser;
      }
    } catch {
      /* analyser optional — waveform falls back to animated */
    }
    return stream;
  }, []);

  // Start MediaRecorder on the (already acquired) stream and begin listening.
  // `arm` controls whether the 3s silence/no-speech timer starts immediately —
  // startSession passes false so the timer only starts once the greeting
  // finishes speaking (see startSilenceWatch below).
  const beginRecording = useCallback(async (arm = true) => {
    if (typeof MediaRecorder === "undefined") {
      setErrorMsg("Voice recording isn't supported on this browser.");
      setPhase("error");
      return;
    }
    try {
      const stream = await acquireStream();
      const mime = MediaRecorder.isTypeSupported("audio/webm") ? "audio/webm" : "";
      const mr = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
      chunksRef.current = [];
      mr.ondataavailable = (e) => e.data.size > 0 && chunksRef.current.push(e.data);
      mr.onstop = () => {
        if (suppressStopRef.current) { suppressStopRef.current = false; return; }
        onStopped(mr.mimeType);
      };
      mediaRef.current = mr;
      mr.start();
      startRef.current = Date.now();
      setPhase("recording");
      setSeconds(0);
      timerRef.current = setInterval(() => setSeconds(Math.floor((Date.now() - startRef.current) / 1000)), 250);
      if (arm) startSilenceWatch();
    } catch {
      setErrorMsg("Microphone permission denied. Enable mic access to use voice.");
      setPhase("error");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [acquireStream]);

  // Open the sheet and start recording immediately — press once to start.
  const startSession = useCallback(async () => {
    reset();
    setOpen(true);
    if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === "undefined") {
      setErrorMsg("Voice recording isn't supported on this browser.");
      setPhase("error");
      return;
    }
    beginRecording(false); // start capturing right away; arm the silence timer once the greeting finishes
    speakThen(`Hi ${firstName}, what can I do for you?`, () => startSilenceWatch());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [firstName, beginRecording]);

  // External trigger — allows a bottom-nav long-press to start the voice
  // session even when this component's own launcher fab is hidden.
  useEffect(() => {
    const handler = () => startSession();
    window.addEventListener("guru:voice-start", handler);
    return () => window.removeEventListener("guru:voice-start", handler);
  }, [startSession]);

  const startRecording = useCallback(async () => {
    reset();
    if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === "undefined") {
      setErrorMsg("Voice recording isn't supported on this browser.");
      setPhase("error");
      return;
    }
    beginRecording();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [beginRecording]);

  const stopRecording = useCallback(() => {
    if (mediaRef.current && mediaRef.current.state !== "inactive") {
      mediaRef.current.stop();
    }
  }, []);

  // Fires after SILENCE_MS with no speech-level audio. If the technician had
  // already started speaking, treat it as "done talking" — stop and process.
  // If nothing was ever heard, cancel the turn without processing.
  const onSilenceElapsed = () => {
    if (!mediaRef.current || mediaRef.current.state !== "recording") return;
    clearSilenceWatch();
    if (hasSpeechRef.current) {
      stopRecording();
    } else {
      close();
    }
  };

  // Watches mic RMS level via the Web Audio analyser and (re)starts a 3s
  // countdown to onSilenceElapsed every time speech-level audio is heard.
  const startSilenceWatch = () => {
    if (!mediaRef.current || mediaRef.current.state !== "recording") return;
    const analyser = analyserRef.current;
    if (!analyser) return; // no Web Audio support — fall back to manual stop only
    clearSilenceWatch();
    hasSpeechRef.current = false;
    silenceTimerRef.current = setTimeout(onSilenceElapsed, SILENCE_MS);
    const buf = new Uint8Array(analyser.fftSize);
    const tick = () => {
      if (!mediaRef.current || mediaRef.current.state !== "recording") { silenceRafRef.current = 0; return; }
      analyser.getByteTimeDomainData(buf);
      let sumSq = 0;
      for (let i = 0; i < buf.length; i++) {
        const v = (buf[i] - 128) / 128;
        sumSq += v * v;
      }
      const rms = Math.sqrt(sumSq / buf.length);
      if (rms > SPEECH_RMS_THRESHOLD) {
        hasSpeechRef.current = true;
        clearTimeout(silenceTimerRef.current);
        silenceTimerRef.current = setTimeout(onSilenceElapsed, SILENCE_MS);
      }
      silenceRafRef.current = requestAnimationFrame(tick);
    };
    silenceRafRef.current = requestAnimationFrame(tick);
  };

  const buildClientNow = () => {
    const now = new Date();
    const pad = (n) => String(n).padStart(2, "0");
    return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}T${pad(now.getHours())}:${pad(now.getMinutes())}:${pad(now.getSeconds())}`;
  };

  // Shared handling of an /agent (audio) or /agent/text (typed) response: either
  // hand off to confirm/views, or — for a clarifying question with nothing to
  // confirm yet — keep the session alive and listen for the technician's reply.
  const handleAgentResult = (data) => {
    if (stoppedRef.current) return; // hard-stopped while this was in flight — no follow-up
    setResult(data);
    if (data.exit) {
      close();
      return;
    }
    const hasPlan = Array.isArray(data.plan) && data.plan.length > 0;
    const hasViews = Array.isArray(data.views) && data.views.length > 0;
    if (hasPlan && data.needsConfirm) {
      speak(data.say);
      setPhase("confirm");
    } else if (hasViews) {
      speak(data.say);
      setPhase("done");
    } else {
      setHistory((h) => [...h, { role: "user", text: data.transcript || "" }, { role: "assistant", text: data.say || "" }].slice(-8));
      if (data.say) speakThen(data.say, () => beginRecording());
      else beginRecording();
    }
  };

  const onStopped = async (mimeType) => {
    cleanupStream();
    setPhase("processing");
    const durationMs = Date.now() - startRef.current;
    const blob = new Blob(chunksRef.current, { type: mimeType || "audio/webm" });
    if (!blob.size) {
      setErrorMsg("No audio captured. Try again.");
      setPhase("error");
      return;
    }
    try {
      const data = await voiceApi.agent(blob, {
        jobId: context.jobId,
        customerId: context.customerId,
        durationMs,
        clientNow: buildClientNow(),
        history,
      });
      if (stoppedRef.current) return;
      handleAgentResult(data);
    } catch (err) {
      if (stoppedRef.current) return;
      const msg = err?.response?.data?.detail || "Something went wrong. Try again.";
      setErrorMsg(msg);
      setPhase("error");
    }
  };

  // Typed alternative to speaking — used during a multi-turn exchange.
  const submitText = async (text) => {
    const t = (text || "").trim();
    if (!t) return;
    if (mediaRef.current && mediaRef.current.state === "recording") {
      suppressStopRef.current = true;
      mediaRef.current.stop();
    }
    cleanupStream();
    setTextInput("");
    setPhase("processing");
    try {
      const data = await voiceApi.agentText({
        transcript: t,
        jobId: context.jobId,
        customerId: context.customerId,
        clientNow: buildClientNow(),
        history,
      });
      if (stoppedRef.current) return;
      handleAgentResult(data);
    } catch (err) {
      if (stoppedRef.current) return;
      const msg = err?.response?.data?.detail || "Something went wrong. Try again.";
      setErrorMsg(msg);
      setPhase("error");
    }
  };

  const buildStepArgs = (step, stepIdx) => {
    if (step.action === "add_estimate_items") {
      const items = (step.resolvedItems || []).map((it, idx) => {
        if (it.status === "ambiguous") return { pricebookId: picks[`${stepIdx}:${idx}`], qty: it.qty };
        if (it.status === "matched") return { pricebookId: it.pricebookId, qty: it.qty };
        return { pricebookId: null, description: it.description, qty: it.qty, unitPrice: it.unitPrice };
      });
      return { ...step.args, items };
    }
    return step.args;
  };

  const confirmAction = async () => {
    setPhase("executing");
    try {
      const steps = (result.plan || []).map((s, i) => ({ action: s.action, args: buildStepArgs(s, i) }));
      const res = await voiceApi.execute({ plan: steps });
      if (stoppedRef.current) return;
      setResult((r) => ({ ...r, executed: res }));
      speak(res.say);
      if (res.ok) toast.success(res.say);
      else toast.error(res.say);
      setPhase("done");
      if (onActionDone) {
        try { onActionDone(result.plan?.[0]?.action, res); } catch { /* non-critical */ }
      }
    } catch (err) {
      if (stoppedRef.current) return;
      const msg = err?.response?.data?.detail || "Couldn't complete that action.";
      setErrorMsg(msg);
      setPhase("error");
    }
  };

  const close = () => {
    stoppedRef.current = true; // invalidate any in-flight response — no follow-up after a hard stop
    if (mediaRef.current && mediaRef.current.state === "recording") {
      suppressStopRef.current = true; // discard whatever audio was captured — never send it for processing
      mediaRef.current.stop();
    }
    cleanupStream();
    stopSpeaking();
    setOpen(false);
    reset();
  };

  const immersive = ["recording", "processing", "executing"].includes(phase);
  const speakingWave = phase === "processing" || phase === "executing";
  const busy = phase === "processing" || phase === "executing";

  return (
    <>
      {/* Floating mic button — hidden when the parent supplies its own launcher (e.g. mobile bottom-nav). */}
      {!hideLauncher && (
      <button
        onClick={startSession}
        data-testid="voice-agent-fab"
        aria-label="Voice assistant"
        className={`fixed z-40 bottom-20 right-4 h-14 w-14 rounded-full bg-gradient-to-br from-blue-600 to-indigo-600 text-white shadow-lg shadow-blue-900/30 flex items-center justify-center active:scale-95 transition-transform ${className}`}
      >
        <Mic className="h-6 w-6" strokeWidth={2.5} />
      </button>
      )}

      {open && (
        <div className="fixed inset-0 z-[80]" data-testid="voice-agent-sheet">
          <div className="absolute inset-0 bg-[#070B16]/90 backdrop-blur-xl" onClick={close} />

          <VoiceCommandsSheet open={helpOpen} onClose={() => setHelpOpen(false)} />

          {immersive ? (
            <div className="relative h-full w-full flex flex-col" data-testid="voice-immersive">
              {/* Top bar */}
              <div className="flex items-center justify-between px-4 pt-5">
                <button onClick={() => setHelpOpen(true)} data-testid="voice-help-btn" aria-label="What can I say?" className="h-9 px-3 rounded-full bg-white/5 border border-white/10 text-blue-200 text-[12.5px] font-semibold flex items-center gap-1.5 active:scale-95 transition-transform">
                  <Sparkles className="h-3.5 w-3.5" /> Help
                </button>
                <div className="text-[14px] font-bold text-white tracking-tight" data-testid="voice-brand">Guru Assistant</div>
                <button onClick={close} data-testid="voice-close-btn" aria-label="Close" className="h-9 w-9 rounded-full bg-white/5 border border-white/10 flex items-center justify-center text-slate-300 active:scale-95 transition-transform">
                  <X className="h-4.5 w-4.5" />
                </button>
              </div>

              {/* Orb + waveform */}
              <div className="flex-1 flex flex-col items-center justify-center px-6 -mt-6">
                <div className="relative flex items-center justify-center w-full" style={{ height: 300 }}>
                  <div className="absolute inset-x-0 top-1/2 -translate-y-1/2 h-44 pointer-events-none">
                    <VoiceWaveform analyserRef={analyserRef} speaking={speakingWave} />
                  </div>
                  {phase === "recording" && (
                    <>
                      <span className="absolute rounded-full border border-blue-400/40 voice-ring" style={{ width: 200, height: 200 }} />
                      <span className="absolute rounded-full border border-blue-400/25 voice-ring" style={{ width: 200, height: 200, animationDelay: "1.3s" }} />
                    </>
                  )}
                  <button
                    onClick={() => { if (phase === "recording") stopRecording(); }}
                    disabled={busy}
                    data-testid="voice-orb"
                    aria-label={phase === "recording" ? "Stop listening and send" : "Listening"}
                    className="relative h-32 w-32 rounded-full flex items-center justify-center text-white voice-orb-glow active:scale-95 transition-transform disabled:opacity-90"
                    style={{ background: "radial-gradient(circle at 50% 36%, #60A5FA 0%, #2563EB 55%, #1D4ED8 100%)" }}
                  >
                    {busy ? <Loader2 className="h-12 w-12 animate-spin" strokeWidth={2} /> : <Mic className="h-12 w-12" strokeWidth={2} />}
                    {phase === "recording" && (
                      <span className="absolute top-2 right-2 h-3.5 w-3.5 rounded-full bg-red-500 border-2 border-white/80 voice-dot" data-testid="voice-recording-indicator" />
                    )}
                  </button>
                </div>

                {/* Caption */}
                <div className="mt-7 min-h-[60px] text-center px-2">
                  <div className="text-[22px] leading-snug font-bold text-white" data-testid="voice-caption">
                    {phase === "recording" && (result?.say || "I'm listening…")}
                    {phase === "processing" && "Understanding…"}
                    {phase === "executing" && "Working on it…"}
                  </div>
                  {phase === "recording" && <div className="mt-1.5 text-[13px] text-blue-200/70" data-testid="voice-mic-hint">Press microphone again to send</div>}
                </div>

                {/* Status pill */}
                <div className="mt-5 inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/[0.07] border border-white/10" data-testid="voice-status-pill">
                  <span className={`h-2 w-2 rounded-full voice-dot ${phase === "recording" ? "bg-red-500" : "bg-blue-400"}`} />
                  <span className="text-[13px] font-semibold text-blue-100">
                    {phase === "recording" ? `Recording… ${seconds}s` : phase === "processing" ? "Thinking…" : "Working…"}
                  </span>
                </div>
              </div>

              {/* Bottom action */}
              <div className="pb-10 px-6 flex flex-col items-center gap-3">
                {phase === "recording" && (
                  <form
                    onSubmit={(e) => { e.preventDefault(); submitText(textInput); }}
                    className="w-full max-w-xs flex items-center gap-2"
                  >
                    <input
                      type="text"
                      value={textInput}
                      onChange={(e) => {
                        setTextInput(e.target.value);
                        // typing counts as activity — keep the silence timer from firing mid-type
                        hasSpeechRef.current = true;
                        if (silenceTimerRef.current) clearTimeout(silenceTimerRef.current);
                        silenceTimerRef.current = setTimeout(onSilenceElapsed, SILENCE_MS);
                      }}
                      placeholder="Or type your answer…"
                      data-testid="voice-text-input"
                      className="flex-1 h-10 px-3.5 rounded-full bg-white/10 border border-white/15 text-white text-[13px] placeholder:text-blue-200/40 focus:outline-none focus:border-blue-400"
                    />
                    <button
                      type="submit"
                      data-testid="voice-text-send-btn"
                      aria-label="Send typed answer"
                      disabled={!textInput.trim()}
                      className="h-10 w-10 shrink-0 rounded-full bg-blue-600 flex items-center justify-center text-white active:scale-95 transition-transform disabled:opacity-40"
                    >
                      <Send className="h-4 w-4" />
                    </button>
                  </form>
                )}
                {phase === "recording" ? (
                  <button onClick={close} data-testid="voice-stop-btn" className="h-12 px-10 rounded-full bg-white/10 border border-white/15 text-white text-[15px] font-semibold active:scale-95 transition-transform">Tap to stop</button>
                ) : (
                  <div className="h-12" />
                )}
              </div>
            </div>
          ) : (
            <div className="absolute inset-x-0 bottom-0 flex items-end sm:inset-0 sm:items-center sm:justify-center">
              <div className="relative w-full sm:max-w-md bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl border border-slate-200 overflow-hidden animate-in slide-in-from-bottom duration-200">
                {/* Header */}
                <div className="px-5 py-3.5 border-b border-slate-100 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="h-8 w-8 rounded-lg bg-gradient-to-br from-blue-600 to-indigo-600 flex items-center justify-center"><Sparkles className="h-4 w-4 text-white" /></div>
                    <div>
                      <div className="text-[18px] font-bold text-slate-900">Guru Assistant</div>
                      {context.customerName && <div className="text-[12px] text-slate-400">On: {context.customerName}</div>}
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    <button onClick={() => setHelpOpen(true)} data-testid="voice-help-btn" aria-label="What can I say?" className="h-7 w-7 rounded-md hover:bg-slate-100 flex items-center justify-center"><HelpCircle className="h-4 w-4 text-slate-500" /></button>
                    <button onClick={close} data-testid="voice-close-btn" className="h-7 w-7 rounded-md hover:bg-slate-100 flex items-center justify-center"><X className="h-4 w-4 text-slate-500" /></button>
                  </div>
                </div>

                <div className="p-5">
                  {/* Transcript + confirm */}
                  {(phase === "confirm" || phase === "done") && result && (
                    <div className="space-y-3">
                      {result.transcript && (
                        <div className="rounded-lg bg-slate-50 border border-slate-100 px-3 py-2">
                          <div className="text-[11px] font-bold uppercase tracking-wide text-slate-400 mb-0.5">You said</div>
                          <div className="text-[16px] text-slate-700" data-testid="voice-transcript">{result.transcript}</div>
                        </div>
                      )}
                      <div className="flex items-start gap-2">
                        <Volume2 className="h-5 w-5 text-blue-600 mt-0.5 shrink-0" />
                        <div className="text-[16px] text-slate-800 font-medium" data-testid="voice-say">{result.say}</div>
                      </div>

                      {Array.isArray(result.views) && result.views.length > 0 && (
                        <div className="space-y-2.5" data-testid="voice-views">
                          {result.views.map((v, i) => (
                            <ViewResultCard key={i} v={v} index={i} navigate={navigate} onClose={close} />
                          ))}
                        </div>
                      )}

                      {phase === "confirm" && (
                        <div className="space-y-2.5" data-testid="voice-plan">
                          {(result.plan || []).map((step, i) => (
                            <PlanStep key={i} step={step} index={i} total={(result.plan || []).length} picks={picks} setPicks={setPicks} />
                          ))}
                        </div>
                      )}

                      {phase === "confirm" && (() => {
                        const unresolved = (result.plan || []).some((s, si) =>
                          s.action === "add_estimate_items" &&
                          (s.resolvedItems || []).some((it, idx) => it.status === "ambiguous" && !picks[`${si}:${idx}`])
                        );
                        return (
                          <div className="flex flex-col gap-2 pt-1">
                            <button onClick={() => { reset(); startRecording(); }} data-testid="voice-cancel-btn" className="w-full h-12 rounded-lg border border-slate-200 text-[18px] font-semibold text-slate-600 hover:bg-slate-50 flex items-center justify-center gap-2"><RotateCcw className="h-5 w-5" /> Redo</button>
                            <button onClick={confirmAction} disabled={unresolved} data-testid="voice-confirm-btn" className="w-full h-12 rounded-lg bg-emerald-600 text-white text-[18px] font-bold hover:bg-emerald-700 disabled:opacity-40 flex items-center justify-center gap-2"><Check className="h-5 w-5" /> {unresolved ? "Pick an item" : (result.plan || []).length > 1 ? "Confirm all" : "Confirm"}</button>
                          </div>
                        );
                      })()}

                      {phase === "done" && result.executed && (
                        <div className="space-y-1.5" data-testid="voice-result">
                          {(result.executed.results || []).map((r, i) => (
                            <div key={i} className={`rounded-lg border px-3 py-2.5 flex items-center justify-between ${r.ok ? "border-emerald-200 bg-emerald-50" : "border-rose-200 bg-rose-50"}`} data-testid={`voice-result-${i}`}>
                              <div className="flex items-center gap-2 min-w-0">
                                {r.ok ? <Check className="h-4 w-4 text-emerald-600 shrink-0" /> : <X className="h-4 w-4 text-rose-500 shrink-0" />}
                                <span className={`text-[13px] font-semibold truncate ${r.ok ? "text-emerald-800" : "text-rose-700"}`}>{r.ok ? (r.result?.label || r.say) : r.error}</span>
                              </div>
                              {r.ok && r.result?.url && (
                                <button onClick={() => { close(); navigate(r.result.url); }} data-testid={`voice-open-result-${i}`} className="text-[12px] font-bold text-emerald-700 hover:underline shrink-0 ml-2">Open</button>
                              )}
                            </div>
                          ))}
                        </div>
                      )}

                      {phase === "done" && !result.executed && !(Array.isArray(result.views) && result.views.length > 0) && (
                        <button onClick={() => { reset(); startRecording(); }} data-testid="voice-again-btn" className="w-full h-10 rounded-lg bg-blue-600 text-white text-[13px] font-bold hover:bg-blue-700 flex items-center justify-center gap-1.5"><Mic className="h-4 w-4" /> Try again</button>
                      )}
                    </div>
                  )}

                  {/* Error */}
                  {phase === "error" && (
                    <div className="flex flex-col items-center py-6">
                      <div className="text-[13px] text-rose-600 text-center" data-testid="voice-error">{errorMsg}</div>
                      <button onClick={() => { reset(); startSession(); }} className="mt-4 h-10 px-5 rounded-lg bg-blue-600 text-white text-[13px] font-bold flex items-center gap-1.5"><Mic className="h-4 w-4" /> Try again</button>
                    </div>
                  )}
                </div>

                {/* footer hint */}
                <button onClick={() => setHelpOpen(true)} data-testid="voice-help-footer-btn" className="w-full px-5 py-2.5 border-t border-slate-100 bg-slate-50/60 text-[11px] text-slate-400 text-center hover:text-blue-600 hover:bg-slate-100 transition-colors">
                  Try: "Add 2 hours labor for this customer" · <span className="font-semibold underline">See all commands</span>
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </>
  );
}

function PlanStep({ step, index, total, picks, setPicks }) {
  return (
    <div data-testid={`voice-plan-step-${index}`}>
      {total > 1 && (
        <div className="flex items-center gap-2 mb-1.5">
          <span className="h-5 w-5 rounded-full bg-blue-600 text-white text-[11px] font-bold flex items-center justify-center">{index + 1}</span>
          <span className="text-[10.5px] font-bold text-slate-400 uppercase tracking-wide">Step {index + 1} of {total}</span>
        </div>
      )}
      {step.action === "add_estimate_items"
        ? <EstimatePreview step={step} stepIdx={index} picks={picks} setPicks={setPicks} />
        : <ActionPreview preview={step.preview} action={step.action} />}
    </div>
  );
}

function EstimatePreview({ step, stepIdx, picks, setPicks }) {
  const meta = ACTION_META.add_estimate_items;
  const Icon = meta.icon;
  const items = step.resolvedItems || [];
  const lineTotal = (it, idx) => {
    if (it.status === "ambiguous") {
      const c = it.candidates.find((x) => x.pricebookId === picks[`${stepIdx}:${idx}`]);
      return c ? c.unitPrice * it.qty : 0;
    }
    return it.unitPrice * it.qty;
  };
  const total = items.reduce((s, it, idx) => s + lineTotal(it, idx), 0);
  return (
    <div className="rounded-lg border border-slate-200 overflow-hidden" data-testid="voice-action-preview">
      <div className="px-3 py-2.5 flex items-center gap-2 border-b border-slate-100" style={{ background: `${meta.color}10` }}>
        <Icon className="h-5 w-5" style={{ color: meta.color }} />
        <span className="text-[18px] font-bold uppercase tracking-wide" style={{ color: meta.color }}>{meta.label}</span>
      </div>
      <div className="px-3 py-2.5 text-[16px] text-slate-700">
        <div className="text-[13px] text-slate-500 mb-2">For <span className="font-semibold text-slate-700">{step.preview?.customer}</span></div>
        <div className="space-y-2">
          {items.map((it, idx) => (
            <ItemRow key={idx} it={it} pickKey={`${stepIdx}:${idx}`} picks={picks} setPicks={setPicks} lineTotal={lineTotal(it, idx)} />
          ))}
        </div>
        <div className="flex items-center justify-between border-t border-slate-100 mt-2.5 pt-1.5 font-bold">
          <span>Total</span><span>${total.toFixed(2)}</span>
        </div>
      </div>
    </div>
  );
}

function ItemRow({ it, pickKey, picks, setPicks, lineTotal }) {
  const tid = pickKey.replace(":", "-");
  if (it.status === "ambiguous") {
    return (
      <div className="rounded-md bg-amber-50 border border-amber-200 px-2.5 py-2" data-testid={`voice-item-ambiguous-${tid}`}>
        <div className="text-[12px] font-semibold text-amber-800 mb-1.5">Did you mean… <span className="text-amber-600">"{it.description}"</span> ×{it.qty}</div>
        <div className="space-y-1">
          {it.candidates.map((c) => {
            const on = picks[pickKey] === c.pricebookId;
            return (
              <button
                key={c.pricebookId}
                onClick={() => setPicks((p) => ({ ...p, [pickKey]: c.pricebookId }))}
                data-testid={`voice-pick-${tid}-${c.pricebookId}`}
                className={`w-full flex items-center justify-between px-2.5 py-1.5 rounded-md border text-left transition-colors ${on ? "bg-blue-600 text-white border-blue-600" : "bg-white border-slate-200 hover:border-blue-300"}`}
              >
                <span className="text-[12.5px] font-medium truncate">{c.name}</span>
                <span className={`text-[12px] ml-2 shrink-0 ${on ? "text-white" : "text-slate-500"}`}>${c.unitPrice.toFixed(2)}{c.unit ? `/${c.unit}` : ""}</span>
              </button>
            );
          })}
        </div>
      </div>
    );
  }
  const isCustom = it.status === "not_in_pricebook";
  return (
    <div className="flex items-center justify-between gap-2" data-testid={`voice-item-${tid}`}>
      <div className="min-w-0">
        <div className="flex items-center gap-1.5">
          <span className="truncate">{it.qty} × {it.description}</span>
          {isCustom ? (
            <span className="text-[9px] font-bold uppercase px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 shrink-0">Not in Pricebook</span>
          ) : (
            <span className="text-[9px] font-bold uppercase px-1.5 py-0.5 rounded bg-blue-100 text-blue-700 shrink-0">Pricebook</span>
          )}
        </div>
        {!isCustom && it.spokenPrice != null && Math.abs(it.spokenPrice - it.unitPrice) > 0.001 && (
          <div className="text-[10.5px] text-slate-400">Pricebook ${it.unitPrice.toFixed(2)} · you said ${it.spokenPrice.toFixed(2)}</div>
        )}
      </div>
      <span className="text-slate-500 shrink-0">${lineTotal.toFixed(2)}</span>
    </div>
  );
}

function ActionPreview({ preview, action }) {
  const meta = ACTION_META[action];
  if (!preview || !meta) return null;
  const Icon = meta.icon;
  return (
    <div className="rounded-lg border border-slate-200 overflow-hidden" data-testid="voice-action-preview">
      <div className="px-3 py-2.5 flex items-center gap-2 border-b border-slate-100" style={{ background: `${meta.color}10` }}>
        <Icon className="h-5 w-5" style={{ color: meta.color }} />
        <span className="text-[18px] font-bold uppercase tracking-wide" style={{ color: meta.color }}>{meta.label}</span>
      </div>
      <div className="px-3 py-2.5 text-[16px] text-slate-700">
        {preview.kind === "message" && (
          <div>
            <div className="text-[13px] text-slate-500 mb-1">To <span className="font-semibold text-slate-700">{preview.recipient}</span></div>
            <div className="rounded bg-slate-50 px-2.5 py-1.5 italic">"{preview.text}"</div>
          </div>
        )}
        {preview.kind === "note" && (
          <div>
            <div className="text-[13px] text-slate-500 mb-1">On <span className="font-semibold text-slate-700">{preview.customer}</span>'s file</div>
            <div className="rounded bg-slate-50 px-2.5 py-1.5 italic">"{preview.note}"</div>
          </div>
        )}
        {(preview.kind === "email_estimate" || preview.kind === "text_estimate") && (
          <div className="text-[16px] text-slate-600">
            {preview.kind === "email_estimate" ? "Email" : "Text"} the estimate to <span className="font-semibold text-slate-800">{preview.customer}</span>
            {preview.estimateRef === "new"
              ? <span className="text-slate-400"> (the one created above)</span>
              : preview.estimateRef ? <span className="text-slate-400"> ({preview.estimateRef})</span> : null}
            <div className="text-[12px] text-slate-400 mt-0.5">Includes a secure review link{preview.kind === "email_estimate" ? " + PDF" : ""}.</div>
          </div>
        )}
        {preview.kind === "email_invoice" && (
          <div className="text-[16px] text-slate-600">
            Email invoice {preview.invoiceId ? <span className="font-semibold text-slate-800">{preview.invoiceId}</span> : <span className="text-slate-400">(most recent)</span>} to <span className="font-semibold text-slate-800">{preview.customer}</span>
            <div className="text-[12px] text-slate-400 mt-0.5">Includes a secure pay link + PDF.</div>
          </div>
        )}
        {preview.kind === "text_invoice" && (
          <div className="text-[16px] text-slate-600">
            Text invoice {preview.invoiceId ? <span className="font-semibold text-slate-800">{preview.invoiceId}</span> : <span className="text-slate-400">(most recent)</span>} to <span className="font-semibold text-slate-800">{preview.customer}</span>
            <div className="text-[12px] text-slate-400 mt-0.5">Sends a secure pay link by SMS.</div>
          </div>
        )}
        {preview.kind === "job_status" && (
          <div className="text-[16px] text-slate-600">
            Change this job's status to <span className="font-bold text-orange-600">{preview.status}</span>
            <div className="text-[12px] text-slate-400 mt-0.5">For {preview.customer}</div>
          </div>
        )}
        {preview.kind === "payment" && (
          <div className="text-[16px] text-slate-600">
            Record a <span className="font-bold text-emerald-700">{preview.amount != null ? `$${Number(preview.amount).toFixed(2)}` : "full-balance"}</span> <span className="font-semibold">{preview.method}</span> payment
            {preview.invoiceId ? <> on <span className="font-semibold text-slate-800">{preview.invoiceId}</span></> : " on their most recent invoice"}
            <div className="text-[12px] text-slate-400 mt-0.5">For {preview.customer}</div>
          </div>
        )}
        {preview.kind === "task" && (
          <div className="text-[16px] text-slate-600">
            <div className="font-semibold text-slate-800">"{preview.title}"</div>
            {preview.assignee && <div className="text-[12px] text-slate-400 mt-0.5">Assigned to {preview.assignee}</div>}
          </div>
        )}
        {preview.kind === "event" && (
          <div className="text-[16px] text-slate-600">
            <div className="font-semibold text-slate-800">"{preview.title}"</div>
            <div className="text-[12px] text-slate-400 mt-0.5">{preview.date} at {preview.time}</div>
          </div>
        )}
        {preview.kind === "schedule_job" && (
          <div className="text-[16px] text-slate-600">
            Schedule a <span className="font-semibold">{preview.jobType}</span> job for <span className="font-semibold text-slate-800">{preview.customer}</span>
            <div className="text-[12px] text-slate-400 mt-0.5">{preview.date} at {preview.time}</div>
          </div>
        )}
        {preview.kind === "new_estimate" && (
          <div className="text-[16px] text-slate-600">
            Create a blank draft estimate for <span className="font-semibold text-slate-800">{preview.customer}</span>
          </div>
        )}
        {preview.kind === "sms_customer" && (
          <div>
            <div className="text-[13px] text-slate-500 mb-1">Text <span className="font-semibold text-slate-700">{preview.customer}</span></div>
            <div className="rounded bg-slate-50 px-2.5 py-1.5 italic">"{preview.text}"</div>
          </div>
        )}
        {preview.kind === "job_note" && (
          <div>
            <div className="text-[13px] text-slate-500 mb-1">On {preview.job ? <span className="font-semibold text-slate-700">job {preview.job}</span> : <span className="text-slate-400">your active job</span>}</div>
            <div className="rounded bg-slate-50 px-2.5 py-1.5 italic">"{preview.note}"</div>
          </div>
        )}
        {preview.kind === "book_appointment" && (
          <div className="text-[16px] text-slate-600">
            Book an appointment for <span className="font-semibold text-slate-800">{preview.customer}</span>
            <div className="text-[12px] text-slate-400 mt-0.5">{preview.date} at {preview.time}</div>
            <div className="text-[11.5px] text-slate-400 mt-1">Creates a company calendar event linked to their customer file.</div>
          </div>
        )}
        {preview.kind === "cancel_appointment" && (
          <div className="text-[16px] text-slate-600">
            Cancel the appointment for <span className="font-semibold text-slate-800">{preview.customer}</span>
            <div className="text-[12px] text-slate-400 mt-0.5">{preview.date}{preview.time ? ` at ${preview.time}` : ""}</div>
          </div>
        )}
        {preview.kind === "reschedule_appointment" && (
          <div className="text-[16px] text-slate-600">
            Reschedule the appointment for <span className="font-semibold text-slate-800">{preview.customer}</span>
            <div className="text-[12px] text-slate-400 mt-0.5">to {preview.date} at {preview.time}</div>
          </div>
        )}
      </div>
    </div>
  );
}

function ViewResultCard({ v, navigate, onClose }) {
  const meta = VIEW_META[v.kind] || VIEW_META.error;
  const Icon = meta.icon;
  const openUrl = (url) => { onClose(); navigate(url); };

  if (v.kind === "error") {
    return (
      <div className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2.5 flex items-center gap-2" data-testid="voice-view-error">
        <X className="h-4 w-4 text-rose-500 shrink-0" />
        <span className="text-[13px] font-semibold text-rose-700">{v.error}</span>
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-slate-200 overflow-hidden" data-testid={`voice-view-${v.kind}`}>
      <div className="px-3 py-2.5 flex items-center justify-between gap-2 border-b border-slate-100" style={{ background: `${meta.color}10` }}>
        <div className="flex items-center gap-2">
          <Icon className="h-5 w-5" style={{ color: meta.color }} />
          <span className="text-[15px] font-bold" style={{ color: meta.color }}>{v.title}</span>
        </div>
        {v.navUrl && (
          <button onClick={() => openUrl(v.navUrl)} data-testid={`voice-view-open-${v.kind}`} className="text-[11px] font-bold uppercase tracking-wide flex items-center gap-1 hover:underline" style={{ color: meta.color }}>
            Open <ExternalLink className="h-3 w-3" />
          </button>
        )}
      </div>
      <div className="px-3 py-2.5 text-[14px] text-slate-700 space-y-1.5">
        {(v.kind === "tasks" || v.kind === "pipeline_cards" || v.kind === "jobs" || v.kind === "invoices" || v.kind === "unread_messages" || v.kind === "appointments" || v.kind === "available_slots") && (v.items || []).length === 0 && (
          <div className="text-[13px] text-slate-400 italic py-1">Nothing to show right now.</div>
        )}

        {v.kind === "tasks" && (v.items || []).map((t) => (
          <div key={t.id} className="flex items-center justify-between gap-2 py-1" data-testid={`voice-view-item-${t.id}`}>
            <span className="truncate font-medium text-slate-800">{t.title}</span>
            <span className="text-[11px] text-slate-400 shrink-0">{t.assignee}</span>
          </div>
        ))}

        {v.kind === "jobs" && (v.items || []).map((j) => (
          <button key={j.id} onClick={() => openUrl(j.url)} data-testid={`voice-view-item-${j.id}`} className="w-full flex items-center justify-between gap-2 py-1.5 border-b border-slate-100 last:border-0 text-left hover:bg-slate-50 rounded px-1">
            <div className="min-w-0">
              <div className="font-semibold text-slate-800 truncate">{j.customer}</div>
              <div className="text-[11px] text-slate-400 truncate">{j.type} · {j.address}</div>
            </div>
            <span className="text-[10px] font-bold uppercase px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 shrink-0">{j.status}</span>
          </button>
        ))}

        {v.kind === "pipeline_cards" && (v.items || []).map((c) => (
          <div key={c.id} className="flex items-center justify-between gap-2 py-1.5 border-b border-slate-100 last:border-0" data-testid={`voice-view-item-${c.id}`}>
            <div className="min-w-0">
              <div className="font-semibold text-slate-800 truncate">{c.title}</div>
              <div className="text-[11px] text-slate-400 truncate">{c.customer} · {c.pipeline}</div>
            </div>
            {c.value > 0 && <span className="text-[12px] font-bold text-emerald-700 shrink-0">${Number(c.value).toLocaleString()}</span>}
          </div>
        ))}

        {v.kind === "invoices" && (v.items || []).map((i) => (
          <button key={i.id} onClick={() => openUrl(i.url)} data-testid={`voice-view-item-${i.id}`} className="w-full flex items-center justify-between gap-2 py-1.5 border-b border-slate-100 last:border-0 text-left hover:bg-slate-50 rounded px-1">
            <div className="min-w-0">
              <div className="font-semibold text-slate-800 truncate">{i.customer}</div>
              <div className="text-[11px] text-slate-400 truncate">{i.id}</div>
            </div>
            <span className="text-[12px] font-bold text-rose-600 shrink-0">${Number(i.balance).toFixed(2)}</span>
          </button>
        ))}

        {v.kind === "revenue_summary" && (
          <div className="flex items-center gap-4 py-1">
            <div>
              <div className="text-[11px] text-slate-400 uppercase font-bold">Booked</div>
              <div className="text-[20px] font-bold text-slate-800">${Number(v.booked).toLocaleString()}</div>
            </div>
            <div>
              <div className="text-[11px] text-slate-400 uppercase font-bold">Collected</div>
              <div className="text-[20px] font-bold text-emerald-700">${Number(v.collected).toLocaleString()}</div>
            </div>
            <div>
              <div className="text-[11px] text-slate-400 uppercase font-bold">Jobs</div>
              <div className="text-[20px] font-bold text-slate-800">{v.jobsCount}</div>
            </div>
          </div>
        )}

        {v.kind === "technicians" && (
          <div className="grid grid-cols-2 gap-2">
            {(v.items || []).map((t) => (
              <div key={t.id} className="flex items-center gap-2 rounded-md bg-slate-50 px-2.5 py-1.5" data-testid={`voice-view-item-${t.id}`}>
                <span className="h-2 w-2 rounded-full shrink-0" style={{ background: TECH_STATUS_COLOR[t.status] || "#94A3B8" }} />
                <div className="min-w-0">
                  <div className="text-[12.5px] font-semibold text-slate-800 truncate">{t.name}</div>
                  <div className="text-[10.5px] text-slate-400">{t.status}</div>
                </div>
              </div>
            ))}
          </div>
        )}

        {v.kind === "call" && (
          <a href={v.telHref} data-testid="voice-view-call-link" className="flex items-center justify-between gap-2 rounded-md bg-emerald-50 border border-emerald-200 px-2.5 py-2 hover:bg-emerald-100">
            <div>
              <div className="font-semibold text-slate-800">{v.customer}</div>
              <div className="text-[12px] text-slate-500">{v.phone}</div>
            </div>
            <Phone className="h-5 w-5 text-emerald-600" />
          </a>
        )}

        {v.kind === "message_nav" && (
          <button onClick={() => openUrl(v.navUrl)} data-testid="voice-view-message-nav-btn" className="w-full flex items-center justify-between gap-2 rounded-md bg-violet-50 border border-violet-200 px-2.5 py-2 hover:bg-violet-100">
            <span className="font-semibold text-slate-800">Compose to {v.customer}</span>
            <MessageSquare className="h-5 w-5 text-violet-600" />
          </button>
        )}

        {v.kind === "unread_messages" && (v.items || []).map((m) => (
          <div key={m.id} className="py-1 border-b border-slate-100 last:border-0" data-testid={`voice-view-item-${m.id}`}>
            <div className="text-[12px] font-semibold text-slate-700">{m.sender}</div>
            <div className="text-[13px] text-slate-600 truncate">{m.text}</div>
          </div>
        ))}

        {v.kind === "appointments" && (v.items || []).map((a) => (
          <div key={a.id} className="flex items-center justify-between gap-2 py-1.5 border-b border-slate-100 last:border-0" data-testid={`voice-view-item-${a.id}`}>
            <div className="min-w-0">
              <div className="font-semibold text-slate-800 truncate">{a.customer || a.title}</div>
              <div className="text-[11px] text-slate-400 truncate">{a.title}</div>
            </div>
            <span className="text-[12px] font-bold text-emerald-700 shrink-0">
              {a.start ? new Date(a.start).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" }) : ""}
            </span>
          </div>
        ))}

        {v.kind === "available_slots" && (
          <div className="flex flex-wrap gap-1.5">
            {(v.items || []).map((s, idx) => (
              <span key={idx} data-testid={`voice-view-slot-${idx}`} className="text-[12.5px] font-semibold px-2.5 py-1 rounded-md bg-sky-50 border border-sky-200 text-sky-700">
                {s.label}
              </span>
            ))}
          </div>
        )}

        {v.kind === "directions" && (
          <a href={v.mapsHref} target="_blank" rel="noreferrer" data-testid="voice-view-directions-link" className="flex items-center justify-between gap-2 rounded-md bg-blue-50 border border-blue-200 px-2.5 py-2 hover:bg-blue-100">
            <div>
              <div className="font-semibold text-slate-800">{v.customer}</div>
              <div className="text-[12px] text-slate-500">{v.address}</div>
            </div>
            <Navigation className="h-5 w-5 text-blue-600" />
          </a>
        )}
      </div>
    </div>
  );
}
